import { useContext, useEffect, useState } from "react";
import styles from "./Placeholder.module.css";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import {
    Box,
    Button,
    Checkbox,
    Collapsible,
    Divider,
    FormLayout,
    Icon,
    InlineGrid,
    Label,
    RadioButton,
    Select,
    Tabs,
    Text,
    TextField,
    Thumbnail,
    Tooltip,
    BlockStack,
    InlineStack,
    List,
    Card,
} from "@shopify/polaris";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { CollectionIcon, DeleteIcon, DragHandleIcon, InfoIcon, ProductIcon, SearchIcon } from "@shopify/polaris-icons";
import { BBProductCardSizes, getProductsDynamicallyAndReturnNewProductsArray } from "../constants";
import { PlaceholderContextType } from "./Placeholder";
import { PlaceholderConfigInterface, ProductObj, VariantsObj } from "../Types";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { CloseIcon } from "./PlaceholderBundle/PlaceholderBundle";
import usePlaceholderMixpanelHook from "./usePlaceholderMixpanelHook";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import TextEditorCart from "../../../components/TextEditorCart/TextEditorCart";
import RenderPages from "../../../components/RenderPages/RenderPages";
import { AppConfigContext } from "../../../context/AppConfigContext";

const PlaceholderAdvanced = ({
    bundleType,
    context,
}: {
    bundleType: string;
    submit: any;
    context: PlaceholderContextType;
}) => {
    const {
        fields: {
            hide_out_of_stock_products,
            show_search_bar,
            limit_max_quantity_per_product,
            allow_pre_selected_products,
            pre_selected_products,
            make_pre_selected_compulsory,
            product_card_mobile_size,
            product_card_desktop_size,
            allow_conditional_logic,
            product_conditions,
            max_quantity_per_product,
            redirect_options,
            show_popup_on_cart_redirect,
            popup_text,
            popup_redirect_url,
            checkout_cta_text,
            limit_show_offer_cta,
            show_offer_cta_pages,
            apply_discount_on_compare_price,
            no_close_accordian_after_completion,
            move_selected_products_first,
            accordion_product_layout,
            bundles_design,
        },
        bundleConfig,
        dispatchBundleConfig,
    } = context as PlaceholderContextType;

    const appConfigContext = useContext(AppConfigContext);

    const [expandBundleVisibility, setExpandBundleVisibility] = useState(false);
    const [expandProductSettings, setExpandProductSettings] = useState(false);
    const [expandCheckoutSettings, setExpandCheckoutSettings] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);

    const tabs = [
        {
            id: "visibility",
            content: "Visibility",
            accessibilityLabel: "Visibility",
            panelID: "visibility-panel",
        },
        {
            id: "pricing",
            content: "Pricing",
            accessibilityLabel: "Pricing",
            panelID: "pricing-panel",
        },
        {
            id: "selection",
            content: "Selection",
            accessibilityLabel: "Selection",
            panelID: "selection-panel",
        },
        {
            id: "flow-rules",
            content: "Flow & Rules",
            accessibilityLabel: "Flow & Rules",
            panelID: "flow-rules-panel",
        },
    ];

    const handleTabChange = (selectedTabIndex: number) => setSelectedTab(selectedTabIndex);

    const SettingRow = ({
        label,
        description,
        checked,
        onChange,
        tooltipContent,
        disabled = false,
    }: {
        label: string;
        description?: string;
        checked: boolean;
        onChange: (val: boolean) => void;
        tooltipContent?: any;
        disabled?: boolean;
    }) => (
        <Box paddingBlock="400">
            <BlockStack gap="200">
                <InlineGrid columns={{ xs: "1fr auto" }} alignItems="center" gap="400">
                    <BlockStack gap="100">
                        <InlineStack gap="100" align="start">
                            <Text as="span" variant="bodyMd" fontWeight="semibold">
                                {label}
                            </Text>
                            {tooltipContent && (
                                <Tooltip content={tooltipContent} width="wide">
                                    <Icon source={InfoIcon} tone="subdued" />
                                </Tooltip>
                            )}
                        </InlineStack>
                        {description && (
                            <Text as="p" variant="bodyMd" tone="subdued">
                                {description}
                            </Text>
                        )}
                    </BlockStack>
                    <ToggleSwitch disabled={disabled} checked={checked} setToggle={() => onChange(!checked)} zoom={1} />
                </InlineGrid>
            </BlockStack>
        </Box>
    );

    const { trackFormSections } = useBundleMixpanelHook();
    const { trackPlaceholderBundle } = usePlaceholderMixpanelHook();

    const updateVisibility = (val: string) => {
        dispatchBundleConfig({
            payload: val,
            key: "limit_show_offer_cta",
        });
        limit_show_offer_cta.onChange(val);
        dispatchBundleConfig({
            payload: [],
            key: "show_offer_cta_pages",
        });
        trackPlaceholderBundle({ cta: "Product Page Bundle Builder Visibility", ctaType: val });
        show_offer_cta_pages.onChange([]);
    };

    const handleProductSearch = async (initialQuery: string = "") => {
        const preSelected = show_offer_cta_pages?.value?.map((product: ProductObj) => ({
            id: product?.id,
            variants: product?.variants.map((variant: VariantsObj) => ({
                id: variant?.id,
            })),
        }));
        const selected: any = await shopify.resourcePicker({
            type: "product",
            multiple: true,
            query: initialQuery,
            action: "select",
            selectionIds: preSelected,
            filter: {
                query: "status:active AND published_status:published",
                variants: false,
            },
        });
        if (selected) {
            const productsArr: ProductObj[] = [];
            selected?.flatMap((product: any) => {
                productsArr.push({
                    id: product?.id,
                    images: product?.images,
                    title: product?.title,
                    handle: product?.handle,
                    quantity: 1,
                    maxProductStock: product?.totalInventory,
                    selectedVariants: [],
                    subscriptions: [],
                    oneTimePurchase: true,
                    isSubscribed: false,
                    variants: product?.variants?.map((variant: VariantsObj) => ({
                        image: { ...variant.image },
                        id: variant?.id,
                        displayName: variant.displayName,

                        title: variant?.title?.toLowerCase() == "default title" ? product?.title : variant?.title,
                        price: variant?.price,
                        quantity: 1,
                        selectedOptions: variant?.selectedOptions,
                        copyPrice: variant.price,
                        compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice : variant.price,
                    })),
                    options: product?.options,
                });
            });
            dispatchBundleConfig({
                payload: productsArr,
                key: "show_offer_cta_pages",
            });
            show_offer_cta_pages.onChange(productsArr);
        }
    };

    const removeProduct = (pId: string = "") => {
        const temp: ProductObj[] = [...show_offer_cta_pages.value];
        const idx = temp.findIndex((p) => p.id == pId);
        if (idx >= 0) {
            temp.splice(idx, 1);
            dispatchBundleConfig({
                payload: temp,
                key: "show_offer_cta_pages",
            });
            show_offer_cta_pages.onChange(temp);
        }
    };

    const handleCollectionSearch = async (initialQuery: string = "") => {
        const preSelected = show_offer_cta_pages?.value?.map((collection: any) => ({
            id: collection.id,
        }));
        const selected = await shopify.resourcePicker({
            type: "collection",
            multiple: true,
            query: initialQuery,
            action: "select",
            selectionIds: preSelected,
            filter: {
                // query: "status:active",
                // variants: false,
            },
        });
        console._log("selected", selected);

        const collectionsArr: any = [];

        if (selected) {
            selected?.map((coll: any) => {
                collectionsArr.push({
                    id: coll?.id,
                    handle: coll?.handle,
                    image: coll.image,
                    title: coll.title,
                });
            });

            dispatchBundleConfig({
                payload: collectionsArr,
                key: "show_offer_cta_pages",
            });
            show_offer_cta_pages.onChange(collectionsArr);
        }
    };

    const removeCollection = (pId: string = "") => {
        const temp = [...show_offer_cta_pages.value];
        let idx: number | undefined = -1;
        idx = temp.findIndex((p: any) => p.id == pId);

        if (idx >= 0) {
            temp.splice(idx, 1);
            dispatchBundleConfig({
                payload: temp,
                key: "show_offer_cta_pages",
            });
            show_offer_cta_pages.onChange(temp);
        }
    };

    return (
        <>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandBundleVisibility((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <div>Bundle Visibility</div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandBundleVisibility ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>
            <Collapsible
                open={expandBundleVisibility}
                id="basic-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <FormLayout>
                    <div className={`${styles.formRow} ${styles.row2}`} style={{ marginTop: -3 }}>
                        <span style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 5 }}>
                            <Label id="widget_icon">Add Bundle on</Label>
                            <Tooltip content={"Boost the visibility of your bundle by adding on the product page."}>
                                <Icon source={InfoIcon} />
                            </Tooltip>
                        </span>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                marginTop: "-18px",
                            }}
                        >
                            <RadioButton
                                label={`All Product Pages`}
                                id={`app`}
                                key={`app`}
                                checked={limit_show_offer_cta.value == "all"}
                                onChange={() => {
                                    updateVisibility("all");
                                }}
                            />
                            <RadioButton
                                label={`Specific Product Pages`}
                                id={`spp`}
                                key={`spp`}
                                checked={limit_show_offer_cta.value == "products"}
                                onChange={() => {
                                    updateVisibility("products");
                                }}
                            />
                            {limit_show_offer_cta?.value == "products" && (
                                <div style={{ marginLeft: 25 }}>
                                    {show_offer_cta_pages?.value?.length ? (
                                        <RenderPages
                                            show_offer_cta_pages={show_offer_cta_pages}
                                            removeProduct={removeProduct}
                                            type={"products"}
                                        />
                                    ) : (
                                        <div
                                            className={styles.emptyProductBox}
                                            style={{ marginBottom: 10 }}
                                            onClick={() => handleProductSearch()}
                                        >
                                            <Button variant={"secondary"}>+ Add Products</Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            <RadioButton
                                label={`Products in Specific Collection`}
                                id={`spc`}
                                key={`spc`}
                                checked={limit_show_offer_cta.value == "collections"}
                                onChange={() => {
                                    updateVisibility("collections");
                                }}
                            />
                            {limit_show_offer_cta?.value == "collections" && (
                                <div style={{ marginLeft: 25 }}>
                                    {show_offer_cta_pages?.value?.length ? (
                                        <RenderPages
                                            show_offer_cta_pages={show_offer_cta_pages}
                                            removeProduct={removeCollection}
                                            type={"collections"}
                                        />
                                    ) : (
                                        <div
                                            className={styles.emptyProductBox}
                                            style={{ marginBottom: 10 }}
                                            onClick={() => handleCollectionSearch()}
                                        >
                                            <Button variant={"secondary"}>+ Add Collections</Button>
                                        </div>
                                    )}
                                </div>
                            )}
                            <RadioButton
                                label={`No Product Pages`}
                                id={`nop`}
                                key={`nop`}
                                checked={limit_show_offer_cta.value == "none"}
                                onChange={() => {
                                    updateVisibility("none");
                                }}
                            />
                        </div>
                    </div>
                </FormLayout>
            </Collapsible>
            {/* <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandBundleMeta((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <div>Add Bundle Image</div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandBundleMeta ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>
            <Collapsible
                open={expandBundleMeta}
                id="basic-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <FormLayout>
                    <div className={`${styles.formRow} ${styles.row2}`}>
                        <div
                            style={{
                                marginTop: 4,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <MediaGallery
                                images={images}
                                products={[]}
                                dispatchBundleConfig={dispatchBundleConfig as any}
                                updateKey={"images"}
                                parentKey={"bundle_as_product"}
                                maxFiles={10}
                            />
                        </div>
                    </div>
                </FormLayout>
            </Collapsible> */}
            {/* Upsell Product Settings */}
            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandProductSettings((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <div>Product Settings</div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandProductSettings ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>
            <Collapsible
                open={expandProductSettings}
                id="basic-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <Box>
                    <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}>
                        <Box padding="400" paddingBlockStart="0">
                            <Card>
                                {selectedTab === 0 && (
                                    <BlockStack>
                                        <SettingRow
                                            label="Hide Out of Stock Products"
                                            description="Automatically hide products that are out of stock."
                                            tooltipContent="Do not show items that are out of stock in the bundle."
                                            checked={hide_out_of_stock_products.value}
                                            onChange={(val: any) => {
                                                dispatchBundleConfig({
                                                    payload: val,
                                                    key: "hide_out_of_stock_products",
                                                });
                                                hide_out_of_stock_products.onChange(val);
                                            }}
                                        />
                                        <Divider />
                                        <SettingRow
                                            label="Show product search"
                                            description="Allow searching products when building or configuring bundles."
                                            tooltipContent="Display a search field inside the bundle so that customers can find items quickly."
                                            checked={show_search_bar.value}
                                            onChange={(val: any) => {
                                                dispatchBundleConfig({
                                                    payload: val,
                                                    key: "show_search_bar",
                                                });
                                                show_search_bar.onChange(val);
                                            }}
                                        />
                                        {bundleConfig?.selected_template_placeholder !== "accordian" && (
                                            <>
                                                <Divider />
                                                <SettingRow
                                                    label="Don't close the accordian after step completion"
                                                    description="Keep the step open even after it is completed."
                                                    tooltipContent="Keep the step open even after it is completed."
                                                    checked={no_close_accordian_after_completion.value}
                                                    onChange={(val: any) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "no_close_accordian_after_completion",
                                                        });
                                                        no_close_accordian_after_completion.onChange(val);
                                                    }}
                                                />
                                            </>
                                        )}
                                        {bundleConfig?.selected_template_placeholder === "accordian" && (
                                            <>
                                                <Divider />
                                                <SettingRow
                                                    label="Move the selected products first"
                                                    description="If enabled, already selected products will move to the top of the list when a step is opened."
                                                    tooltipContent="If enabled, already selected products will move to the top of the list when a step is opened."
                                                    checked={move_selected_products_first.value}
                                                    onChange={(val: any) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "move_selected_products_first",
                                                        });
                                                        move_selected_products_first.onChange(val);
                                                    }}
                                                />
                                                <Divider />
                                                <Box paddingBlock="400">
                                                    <BlockStack gap="200">
                                                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                                                            Accordion Product Layout
                                                        </Text>
                                                        <Select
                                                            label=""
                                                            options={[
                                                                { label: "Grid View", value: "grid" },
                                                                { label: "Horizontal Scroll", value: "scroll" },
                                                            ]}
                                                            value={accordion_product_layout.value}
                                                            onChange={(val: any) => {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "accordion_product_layout",
                                                                });
                                                                accordion_product_layout.onChange(val);
                                                            }}
                                                        />
                                                    </BlockStack>
                                                </Box>
                                            </>
                                        )}
                                        <Divider />
                                        <SettingRow
                                            label={"Product Image Auto Height"}
                                            description={
                                                "If enabled, product image height will be auto and max height will be 350px for desktop and 250px for mobile."
                                            }
                                            checked={bundles_design.is_product_img_height_auto.value}
                                            onChange={(val) => {
                                                bundles_design.is_product_img_height_auto.onChange(val);
                                                dispatchBundleConfig({
                                                    payload: {
                                                        is_product_img_height_auto: val,
                                                    },
                                                    isComplex: true,
                                                    key: "bundles_design",
                                                });
                                            }}
                                        />
                                    </BlockStack>
                                )}

                                {selectedTab === 1 && (
                                    <BlockStack>
                                        <SettingRow
                                            label="Apply discount on the Compare-at Price"
                                            description="Calculate discount based on Compare-at Price instead of selling price."
                                            tooltipContent={
                                                <BlockStack gap="200">
                                                    <Text as="p">
                                                        Your discount will be calculated based on the product’s
                                                        Compare-at Price instead of the actual selling price.
                                                    </Text>
                                                    <Text as="p" variant="bodyMd" fontWeight="bold">
                                                        Important Note
                                                    </Text>
                                                    <Text as="p">
                                                        Your discount must be{" "}
                                                        <Text as="span" fontWeight="semibold">
                                                            greater than the discount Shopify already shows &nbsp;
                                                        </Text>
                                                        between the Compare-at Price and the Actual Price.
                                                    </Text>
                                                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                                                        Example:
                                                    </Text>
                                                    <List type="bullet">
                                                        <List.Item>Compare-at Price: $100</List.Item>
                                                        <List.Item>Actual Price: $90</List.Item>
                                                        <List.Item>
                                                            → Shopify is already showing a 10% discount.
                                                        </List.Item>
                                                    </List>

                                                    <Text as="p" variant="bodyMd">
                                                        To apply your custom discount correctly, you must set a discount
                                                        <Text as="span" fontWeight="semibold">
                                                            &nbsp;greater than 10% in app
                                                        </Text>
                                                    </Text>
                                                </BlockStack>
                                            }
                                            checked={apply_discount_on_compare_price.value}
                                            onChange={(val: any) => {
                                                dispatchBundleConfig({
                                                    payload: val,
                                                    key: "apply_discount_on_compare_price",
                                                });
                                                apply_discount_on_compare_price.onChange(val);
                                            }}
                                        />
                                    </BlockStack>
                                )}

                                {selectedTab === 2 && (
                                    <BlockStack>
                                        <SettingRow
                                            label="Max Quantity Selection Per Product"
                                            description="Sets the maximum number of units a customer can add for each product."
                                            checked={limit_max_quantity_per_product.value}
                                            onChange={(val: any) => {
                                                dispatchBundleConfig({
                                                    payload: val,
                                                    key: "limit_max_quantity_per_product",
                                                });
                                                limit_max_quantity_per_product.onChange(val);
                                            }}
                                        />
                                        {limit_max_quantity_per_product.value && (
                                            <Box paddingBlockStart="100" paddingBlockEnd="300">
                                                <TextField
                                                    label="Max Quantity Selection Per Product"
                                                    labelHidden
                                                    disabled={!limit_max_quantity_per_product?.value}
                                                    placeholder={""}
                                                    autoComplete="off"
                                                    type="number"
                                                    step={1}
                                                    min={0}
                                                    max={20}
                                                    onChange={(val) => {
                                                        let numVal = +val;
                                                        if (numVal > 20) {
                                                            numVal = 20;
                                                        }
                                                        dispatchBundleConfig({
                                                            payload: +numVal,
                                                            key: "max_quantity_per_product",
                                                        });
                                                        max_quantity_per_product.onChange(+numVal);
                                                    }}
                                                    value={max_quantity_per_product?.value?.toString()}
                                                />
                                            </Box>
                                        )}
                                        <Divider />
                                        <SettingRow
                                            label="Pre-select items by default"
                                            description="Automatically selects these items when the bundle loads."
                                            tooltipContent="Automatically selects these items when the bundle loads."
                                            checked={allow_pre_selected_products.value}
                                            onChange={(val: any) => {
                                                dispatchBundleConfig({
                                                    payload: val,
                                                    key: "allow_pre_selected_products",
                                                });
                                                allow_pre_selected_products.onChange(val);

                                                if (val === false) {
                                                    dispatchBundleConfig({
                                                        payload: [],
                                                        key: "pre_selected_products",
                                                    });
                                                    pre_selected_products.onChange([]);
                                                }
                                            }}
                                        />
                                        {allow_pre_selected_products.value && (
                                            <Box paddingBlockStart="200">
                                                <PreSelectedProducts
                                                    pre_selected_products={pre_selected_products}
                                                    make_pre_selected_compulsory={make_pre_selected_compulsory}
                                                    dispatchBundleConfig={dispatchBundleConfig}
                                                    sft={appConfigContext.sft}
                                                    bundleConfig={bundleConfig}
                                                />
                                            </Box>
                                        )}
                                        <Divider />
                                        <Box paddingBlock={"400"}>
                                            <BlockStack gap="400">
                                                <Text as="span" variant="bodyMd" fontWeight="semibold">
                                                    Product Card Size
                                                </Text>
                                                <Select
                                                    label={"Product Card Desktop Size"}
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "product_card_desktop_size",
                                                        });
                                                        trackPlaceholderBundle({
                                                            cta: "Product Card Desktop Size",
                                                            ctaType: val,
                                                        });
                                                        product_card_desktop_size.onChange(val);
                                                    }}
                                                    value={product_card_desktop_size.value}
                                                    options={BBProductCardSizes}
                                                />
                                                <Select
                                                    label={"Product Card Mobile Size"}
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "product_card_mobile_size",
                                                        });
                                                        trackPlaceholderBundle({
                                                            cta: "Product Card Mobile Size",
                                                            ctaType: val,
                                                        });
                                                        product_card_mobile_size.onChange(val);
                                                    }}
                                                    value={product_card_mobile_size.value}
                                                    options={BBProductCardSizes}
                                                />
                                            </BlockStack>
                                        </Box>
                                    </BlockStack>
                                )}

                                {selectedTab === 3 && (
                                    <BlockStack>
                                        <SettingRow
                                            label="Enable conditional rules"
                                            description="Show or hide items based on rules (Advanced users only)."
                                            tooltipContent="Show or hide items based on rules (e.g., ‘If product A selected → show product B’). Advanced users only."
                                            checked={allow_conditional_logic.value}
                                            onChange={(val: any) => {
                                                dispatchBundleConfig({
                                                    payload: val,
                                                    key: "allow_conditional_logic",
                                                });
                                                allow_conditional_logic.onChange(val);

                                                if (product_conditions.value.length === 0) {
                                                    const emptyCondition = [
                                                        {
                                                            included_products: [],
                                                            hidden_products: [],
                                                        },
                                                    ];
                                                    product_conditions.onChange(emptyCondition);
                                                    dispatchBundleConfig({
                                                        payload: emptyCondition,
                                                        key: "product_conditions",
                                                    });
                                                }
                                            }}
                                        />
                                        {allow_conditional_logic.value && (
                                            <Box paddingBlockStart="200">
                                                <ConditionalProducts
                                                    product_conditions={product_conditions}
                                                    dispatchBundleConfig={dispatchBundleConfig}
                                                />
                                            </Box>
                                        )}
                                    </BlockStack>
                                )}
                            </Card>
                        </Box>
                    </Tabs>
                </Box>
            </Collapsible>
            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>

            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandCheckoutSettings((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <div>Checkout Settings</div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandCheckoutSettings ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>

            <Collapsible
                open={expandCheckoutSettings}
                id="basic-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <FormLayout>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <RadioButton
                            label="Redirect to Checkout"
                            helpText=""
                            checked={redirect_options.value === "checkout"}
                            name="checkout_settings"
                            onChange={() => {
                                redirect_options.onChange("checkout");
                                dispatchBundleConfig({
                                    payload: "checkout",
                                    key: "redirect_options",
                                });

                                checkout_cta_text.onChange("Go to Checkout");
                                dispatchBundleConfig({
                                    payload: "Go to Checkout",
                                    key: "checkout_cta_text",
                                });
                            }}
                        />
                        <RadioButton
                            label="Redirect to Cart"
                            helpText=""
                            checked={redirect_options.value === "cart"}
                            name="checkout_settings"
                            onChange={() => {
                                redirect_options.onChange("cart");
                                dispatchBundleConfig({
                                    payload: "cart",
                                    key: "redirect_options",
                                });
                                checkout_cta_text.onChange("Add to Cart");
                                dispatchBundleConfig({
                                    payload: "Add to Cart",
                                    key: "checkout_cta_text",
                                });
                            }}
                        />

                        {redirect_options.value == "cart" && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                    padding: "10px",
                                    borderRadius: "8px",
                                    backgroundColor: "#f9f9f9",
                                }}
                            >
                                <div className={styles.subStepTogglerWrapper}>
                                    <ToggleSwitch
                                        checked={show_popup_on_cart_redirect.value}
                                        setToggle={(e: any) => {
                                            e?.preventDefault();
                                            e.stopPropagation();
                                            const newVal = !show_popup_on_cart_redirect.value;
                                            show_popup_on_cart_redirect.onChange(newVal);
                                            dispatchBundleConfig({
                                                payload: newVal,
                                                key: "show_popup_on_cart_redirect",
                                            });
                                        }}
                                        disabled={false}
                                        zoom={0.8}
                                    />{" "}
                                    <Label id={"hello"}>Show Popup on Add to Cart Click</Label>
                                </div>

                                {show_popup_on_cart_redirect?.value && (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "16px",
                                        }}
                                    >
                                        <TextEditorCart
                                            placeholder="Yay! Your bundle has been added to cart!"
                                            value={popup_text.value}
                                            onChange={(value: any, _: any, source: string) => {
                                                if (source == "user") {
                                                    const cleanedValue = value === "<p><br></p>" ? "" : value;
                                                    popup_text.onChange(cleanedValue);
                                                    dispatchBundleConfig({
                                                        payload: cleanedValue,
                                                        key: "popup_text",
                                                    });
                                                }
                                            }}
                                            title={"Popup Text"}
                                            _style={{
                                                zIndex: 10,
                                            }}
                                        />
                                        <div
                                            style={{
                                                zIndex: 0,
                                            }}
                                        >
                                            <TextField
                                                label={"Redirect URL for Continue Shopping CTA"}
                                                placeholder={"https://example.com"}
                                                autoComplete="off"
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "popup_redirect_url",
                                                    });
                                                    popup_redirect_url.onChange(val);
                                                }}
                                                error={popup_redirect_url.error}
                                                value={popup_redirect_url.value}
                                                onBlur={popup_redirect_url?.onBlur}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </FormLayout>
            </Collapsible>

            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>
        </>
    );
};

export default PlaceholderAdvanced;

const PreSelectedProducts = ({
    pre_selected_products,
    make_pre_selected_compulsory,
    dispatchBundleConfig,
    sft,
    bundleConfig,
}: {
    pre_selected_products: any;
    make_pre_selected_compulsory: any;
    dispatchBundleConfig: any;
    sft: string;
    bundleConfig: PlaceholderConfigInterface | undefined;
}) => {
    const [productSearchQuery, setProductSearchQuery] = useState("");
    const syncPreSelectedProducts = (products: ProductObj[] | any[]) => {
        pre_selected_products.onChange(products);
        dispatchBundleConfig({
            payload: products,
            key: "pre_selected_products",
        });
    };

    const handleProductSearch = async () => {
        const preSelected = pre_selected_products.value?.map((product: ProductObj) => ({
            id: product?.id,
        }));
        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: true,
            query: productSearchQuery,
            action: "select",
            selectionIds: preSelected,
            filter: {
                query: "status:active AND published_status:published",
                variants: false,
            },
        });
        if (selected) {
            const productsArr: any = [...(pre_selected_products.value || [])];
            selected?.map((product: any) => {
                const existingProduct = productsArr?.find((p: any) => p.id === product.id);

                if (!existingProduct) {
                    productsArr.push({
                        id: product?.id,
                        title: product?.title,
                        quantity: existingProduct?.quantity || 1,
                        image: product?.images?.[0]?.originalSrc,
                        variants: product.variants.map((v: any) => {
                            return {
                                id: v.id,
                            };
                        }),
                    });
                }
            });
            setProductSearchQuery("");

            try {
                const newProductsArr = await getProductsDynamicallyAndReturnNewProductsArray(productsArr, sft);
                dispatchBundleConfig({
                    payload: newProductsArr,
                    key: "pre_selected_products",
                });
                pre_selected_products.onChange(newProductsArr);
            } catch (error) {
                console._log("Couldn't fetch products:", error);
                dispatchBundleConfig({
                    payload: productsArr,
                    key: "pre_selected_products",
                });
                pre_selected_products.onChange(productsArr);
            }
        }
    };

    const removeProducts = (pId: string = "") => {
        const temp = [...pre_selected_products.value];
        let idx: number | undefined = -1;
        idx = temp.findIndex((p: any) => p.id == pId);

        if (idx >= 0) {
            temp.splice(idx, 1);
            syncPreSelectedProducts(temp);
        }
    };

    const handleDragEnd = (result: DropResult) => {
        const { source, destination } = result;
        if (!destination || source.index === destination.index) return;

        const updatedProducts = [...pre_selected_products.value];
        const [movedItem] = updatedProducts.splice(source.index, 1);
        updatedProducts.splice(destination.index, 0, movedItem);
        syncPreSelectedProducts(updatedProducts);
    };

    useEffect(() => {
        if (!bundleConfig || !pre_selected_products?.value) return;

        const stepProducts = bundleConfig.steps.flatMap((s) => {
            if (s?.enable_subSteps) {
                return s?.subSteps?.flatMap((ss) => ss.products.map((p) => p.id));
            } else {
                return s.products.map((p) => p.id);
            }
        });

        const stepCollections = bundleConfig.steps.flatMap((s) => {
            if (s?.enable_subSteps) {
                return s?.subSteps?.flatMap((ss) => ss.collections.map((c) => c.id));
            } else {
                return s.collections.map((c) => c.id);
            }
        });

        const newPreSelectedProductsList = bundleConfig.pre_selected_products.filter((product) => {
            const isDirectProduct = stepProducts.includes(product.id);
            // @ts-expect-error hack
            const isInAllowedCollection = product?.collections?.some((c) => stepCollections.includes(c));
            return isDirectProduct || isInAllowedCollection;
        });

        if (newPreSelectedProductsList.length !== pre_selected_products?.value?.length) {
            pre_selected_products.onChange(newPreSelectedProductsList);
            dispatchBundleConfig({
                payload: newPreSelectedProductsList,
                key: "pre_selected_products",
            });
            shopify.toast.show("Only products included in the bundle can be set as preselected", {
                isError: true,
                duration: 1500,
            });
        }
    }, [pre_selected_products?.value]);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                padding: "10px",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
            }}
        >
            <h2>Add Pre-selected Products</h2>
            <TextField
                label={"Search Product"}
                labelHidden
                placeholder="Search Products"
                autoComplete={"off"}
                value={productSearchQuery}
                onChange={(val) => {
                    setProductSearchQuery(val);
                }}
                prefix={<Icon source={SearchIcon} />}
                size="slim"
                connectedRight={
                    <div style={{ marginLeft: "8px" }}>
                        <Button onClick={() => handleProductSearch()} size="slim">
                            Browse
                        </Button>
                    </div>
                }
            />

            {pre_selected_products?.value?.length > 0 && (
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="placeholder-preselected-products">
                        {(provided) => (
                            <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "5px",
                                    border: "1px solid rgba(227,227,227,1)",
                                    borderRadius: "12px",
                                    backgroundColor: "#ffffff",
                                    padding: "10px",
                                }}
                            >
                                {pre_selected_products?.value?.map((p: any, index: number) => (
                                    <Draggable
                                        key={p?.id || `pre-selected-${index}`}
                                        draggableId={(p?.id || `pre-selected-${index}`).toString()}
                                        index={index}
                                    >
                                        {(draggableProvided, snapshot) => (
                                            <div
                                                ref={draggableProvided.innerRef}
                                                {...draggableProvided.draggableProps}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: "10px",
                                                    padding: "4px 0",
                                                    borderRadius: "8px",
                                                    backgroundColor: snapshot.isDragging ? "#f5f7fa" : "transparent",
                                                    ...draggableProvided.draggableProps.style,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "flex-start",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                        flexGrow: "1",
                                                        maxWidth: "68%",
                                                    }}
                                                >
                                                    <span
                                                        {...draggableProvided.dragHandleProps}
                                                        style={{
                                                            cursor: "grab",
                                                            color: "#7A7A7A",
                                                            fontSize: "18px",
                                                            userSelect: "none",
                                                            lineHeight: 1,
                                                        }}
                                                        title="Drag to reorder"
                                                    >
                                                        <Icon source={DragHandleIcon} />
                                                    </span>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            overflow: "hidden",
                                                        }}
                                                    >
                                                        <Thumbnail
                                                            source={p?.image || CollectionIcon}
                                                            alt={p?.title}
                                                            size={"small"}
                                                        />
                                                    </div>
                                                    <p>{p?.title}</p>
                                                </div>

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                        minWidth: "120px",
                                                        maxWidth: "120px",
                                                    }}
                                                    onMouseDownCapture={(e) => e.stopPropagation()}
                                                    onPointerDownCapture={(e) => e.stopPropagation()}
                                                    onTouchStartCapture={(e) => e.stopPropagation()}
                                                >
                                                    <Label id="">Qty</Label>
                                                    <TextField
                                                        type="number"
                                                        step={1}
                                                        value={p?.quantity?.toString() || "1"}
                                                        min={1}
                                                        onChange={(value) => {
                                                            const newQty = parseInt(value) || 1;
                                                            const updatedProducts = [...pre_selected_products.value];
                                                            updatedProducts[index] = {
                                                                ...updatedProducts[index],
                                                                quantity: Math.max(1, newQty),
                                                            };
                                                            syncPreSelectedProducts(updatedProducts);
                                                        }}
                                                        autoComplete="off"
                                                        labelHidden
                                                        label="Qty"
                                                    />
                                                </div>

                                                <div
                                                    onClick={() => {
                                                        removeProducts(p.id);
                                                    }}
                                                    style={{
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <CloseIcon />
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            )}

            <Checkbox
                label={"Make pre-selected products compulsary"}
                {...make_pre_selected_compulsory}
                value=""
                checked={make_pre_selected_compulsory.value}
                onChange={(val) => {
                    dispatchBundleConfig({
                        payload: val,
                        key: "make_pre_selected_compulsory",
                    });
                    make_pre_selected_compulsory.onChange(val);
                }}
            />
        </div>
    );
};

const ConditionalProducts = ({
    product_conditions,
    dispatchBundleConfig,
}: {
    product_conditions: any;
    dispatchBundleConfig: any;
}) => {
    const [showProductModal, setShowProductModal] = useState(false);
    const [activeConditionIndex, setActiveConditionIndex] = useState<number | null>(null);
    const [activeProductType, setActiveProductType] = useState<"included_products" | "hidden_products" | null>(null);

    const handleProductSearch = async (conditionIdx: number, type: "included_products" | "hidden_products") => {
        const currentList = product_conditions.value[conditionIdx][type];

        const preSelected = currentList?.map((product: ProductObj) => ({
            id: product?.id,
            variants: product?.variants.map((variant: any) => ({
                id: variant?.id,
            })),
        }));

        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: true,
            query: "",
            action: "select",
            selectionIds: preSelected,
            filter: {
                query: "status:active AND published_status:published",
                variants: true,
            },
        });

        if (selected) {
            const productsArr: ProductObj[] = [];
            selected?.flatMap((product: any) => {
                productsArr.push({
                    id: product?.id,
                    images: product?.images,
                    title: product?.title,
                    handle: product?.handle,
                    quantity: 1,
                    maxProductStock: product?.totalInventory,
                    selectedVariants: [],
                    subscriptions: [],
                    oneTimePurchase: true,
                    isSubscribed: false,
                    variants: product?.variants?.map((variant: VariantsObj) => ({
                        image: { ...variant.image },
                        id: variant?.id,
                        displayName: variant.displayName,
                        title: variant?.title?.toLowerCase() == "default title" ? product?.title : variant?.title,
                        price: variant?.price,
                        quantity: 1,
                        selectedOptions: variant?.selectedOptions,
                        copyPrice: variant.price,
                        compareAtPrice: variant?.compareAtPrice ? variant.compareAtPrice : variant.price,
                    })),
                    options: product?.options,
                });
            });

            const updatedConditions = [...product_conditions.value];
            updatedConditions[conditionIdx] = {
                ...updatedConditions[conditionIdx],
                [type]: productsArr,
            };

            product_conditions.onChange(updatedConditions);
            dispatchBundleConfig({
                payload: updatedConditions,
                key: "product_conditions",
            });
        }
    };

    const openProductModal = (index: number, type: "included_products" | "hidden_products") => {
        setActiveConditionIndex(index);
        setActiveProductType(type);
        setShowProductModal(true);
    };

    const handleRemoveConditionStep = (conditionIdx: number) => {
        const updatedConditions = [...product_conditions.value];
        updatedConditions.splice(conditionIdx, 1);

        product_conditions.onChange(updatedConditions);
        dispatchBundleConfig({
            payload: updatedConditions,
            key: "product_conditions",
        });
    };

    const handleProductDelete = (productId: string) => {
        if (activeConditionIndex === null || !activeProductType) return;

        const updatedConditions = [...product_conditions.value];
        const productList = updatedConditions[activeConditionIndex][activeProductType];

        const tmp = productList.filter((p: ProductObj) => p.id !== productId);

        updatedConditions[activeConditionIndex] = {
            ...updatedConditions[activeConditionIndex],
            [activeProductType]: tmp,
        };

        product_conditions.onChange(updatedConditions);
        dispatchBundleConfig({
            payload: updatedConditions,
            key: "product_conditions",
        });
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                padding: "10px",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
            }}
        >
            <h2>Conditional Products</h2>

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                }}
            >
                {product_conditions?.value?.map(
                    (_p: { included_products: ProductObj[]; hidden_products: ProductObj[] }, index: number) => (
                        <div
                            key={index}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                border: "1px solid #ccc",
                                padding: "14px 28px 14px 14px",
                                borderRadius: "6px",
                                position: "relative",
                            }}
                        >
                            <span
                                style={{
                                    position: "absolute",
                                    top: "2px",
                                    right: "2px",
                                }}
                            >
                                <Button
                                    onClick={() => handleRemoveConditionStep(index)}
                                    icon={DeleteIcon}
                                    variant="plain"
                                    tone="critical"
                                    accessibilityLabel="Delete"
                                    size="slim"
                                ></Button>
                            </span>

                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "5px",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <p>If Selected Products Include</p>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "10px",
                                        alignItems: "center",
                                    }}
                                >
                                    <Button onClick={() => handleProductSearch(index, "included_products")}>
                                        Select Products
                                    </Button>
                                    <p
                                        style={{
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                            color: "#007ace",
                                            minWidth: "80px",
                                        }}
                                        onClick={() => openProductModal(index, "included_products")}
                                    >
                                        {_p.included_products.length} selected
                                    </p>
                                </div>
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "5px",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <p>Hidden Products</p>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "10px",
                                        alignItems: "center",
                                    }}
                                >
                                    <Button onClick={() => handleProductSearch(index, "hidden_products")}>
                                        Select Products
                                    </Button>
                                    <p
                                        style={{
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                            color: "#007ace",
                                            minWidth: "80px",
                                        }}
                                        onClick={() => openProductModal(index, "hidden_products")}
                                    >
                                        {_p.hidden_products.length} selected
                                    </p>
                                </div>
                            </div>
                        </div>
                    ),
                )}

                <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => {
                        const updatedConditions = [...product_conditions.value];
                        updatedConditions.push({
                            included_products: [],
                            hidden_products: [],
                        });

                        product_conditions.onChange(updatedConditions);
                        dispatchBundleConfig({
                            payload: updatedConditions,
                            key: "product_conditions",
                        });
                    }}
                >
                    + Add Conditional Logic
                </Button>
            </div>

            <CustomModal
                title={activeProductType === "included_products" ? "Included Products" : "Hidden Products"}
                open={showProductModal}
                onClose={() => setShowProductModal(false)}
                secondaryActions={[
                    {
                        content: "Close",
                        onAction: () => setShowProductModal(false),
                    },
                ]}
                primaryAction={{
                    content:
                        activeConditionIndex !== null &&
                        activeProductType &&
                        product_conditions?.value?.[activeConditionIndex]?.[activeProductType]?.length > 0
                            ? "Edit Products"
                            : "Add Products",
                    onAction: () => {
                        if (activeConditionIndex !== null && activeProductType)
                            handleProductSearch(activeConditionIndex, activeProductType);
                    },
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {activeConditionIndex !== null &&
                    activeProductType &&
                    product_conditions?.value?.[activeConditionIndex]?.[activeProductType]?.length > 0 ? (
                        product_conditions.value[activeConditionIndex][activeProductType].map((product: ProductObj) => (
                            <div
                                key={product.id}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "8px 12px",
                                    borderBottom: "1px solid #eee",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "10px",
                                        alignItems: "center",
                                    }}
                                >
                                    <Thumbnail
                                        source={product?.images[0]?.originalSrc || ProductIcon}
                                        alt={product?.images[0]?.altText || ""}
                                        size={"small"}
                                    />
                                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                        <span>{product.title}</span>
                                        <span style={{ fontSize: "10px" }}>
                                            {product.variants.length} variants included
                                        </span>
                                    </div>
                                </div>

                                <Button
                                    onClick={() => handleProductDelete(product.id)}
                                    icon={DeleteIcon}
                                    variant="plain"
                                    tone="critical"
                                    accessibilityLabel="Delete"
                                ></Button>
                            </div>
                        ))
                    ) : (
                        <p>No products added.</p>
                    )}
                </div>
            </CustomModal>
        </div>
    );
};
